import React from 'react';
import { createPortal } from 'react-dom';

interface TagColorPickerProps {
    anchorRect: DOMRect;
    currentColor?: string;
    onColorSelect: (color: string) => void;
    onClose: () => void;
}

const PRESET_COLORS = [
    { name: 'Violet', value: '#8b5cf6' },
    { name: 'Blue', value: '#3b82f6' },
    { name: 'Emerald', value: '#10b981' },
    { name: 'Amber', value: '#f59e0b' },
    { name: 'Rose', value: '#f43f5e' },
    { name: 'Indigo', value: '#6366f1' },
    { name: 'Teal', value: '#14b8a6' },
    { name: 'Orange', value: '#f97316' },
];

export const TagColorPicker: React.FC<TagColorPickerProps> = ({
    anchorRect,
    currentColor,
    onColorSelect,
    onClose
}) => {
    const pickerRef = React.useRef<HTMLDivElement>(null);

    // Position calculation
    const [position, setPosition] = React.useState({ top: 0, left: 0 });

    React.useEffect(() => {
        if (!pickerRef.current) return;

        const pickerRect = pickerRef.current.getBoundingClientRect();
        let top = anchorRect.bottom + 8;
        let left = anchorRect.left;

        // Flip up if too close to bottom
        if (top + pickerRect.height > window.innerHeight - 20) {
            top = anchorRect.top - pickerRect.height - 8;
        }

        // Flip left if too close to right edge
        if (left + pickerRect.width > window.innerWidth - 20) {
            left = anchorRect.right - pickerRect.width;
        }

        setPosition({ top, left });
    }, [anchorRect]);

    // Click outside to close
    React.useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    // Escape to close
    React.useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    return createPortal(
        <div
            ref={pickerRef}
            className="fixed z-[10000] bg-white dark:bg-[#1c1e24] rounded-xl shadow-[0_12px_48px_rgba(100,120,140,0.2)] dark:shadow-[0_12px_48px_rgba(0,0,0,0.5)] p-3 border border-slate-200 dark:border-white/10"
            style={{ top: position.top, left: position.left }}
        >
            <div className="grid grid-cols-4 gap-2">
                {PRESET_COLORS.map((color) => (
                    <button
                        key={color.value}
                        className="w-10 h-10 rounded-lg border-2 transition-all hover:scale-110"
                        style={{
                            backgroundColor: color.value,
                            borderColor: currentColor === color.value ? color.value : 'transparent',
                        }}
                        onClick={() => {
                            onColorSelect(color.value);
                            onClose();
                        }}
                        title={color.name}
                    />
                ))}
            </div>
            <div className="mt-2 pt-2 border-t border-slate-200 dark:border-white/10">
                <button
                    className="w-full text-xs text-slate-600 dark:text-white/60 hover:text-slate-800 dark:hover:text-white transition-colors"
                    onClick={() => {
                        onColorSelect('#8b5cf6'); // Reset to default violet
                        onClose();
                    }}
                >
                    Reset to default
                </button>
            </div>
        </div>,
        document.body
    );
};
